import React, { useEffect, useState } from "react";
import { Wrench, AlertTriangle, CheckCircle2, XCircle, BatteryCharging } from "lucide-react";
import { crm } from "@/lib/crm";
import { formatPrice } from "@/lib/format";

const HULK = "#39FF6A";

// Electric Rehab panel — shown on a listing flagged as a battery-repair opportunity.
// Prioritises the battery health report and highlights defective modules in red
// (only when a certified passport exists for this VIN).
export const RehabPanel = ({ car }) => {
  const rehab = car?.rehab;
  const [report, setReport] = useState(null);
  const reportId = car?.battery?.report_id;

  useEffect(() => {
    let alive = true;
    if (!reportId) { setReport(null); return; }
    crm(`/verify/${reportId}`, { noCache: true })
      .then((r) => { if (alive) setReport(r?.report || null); })
      .catch(() => { if (alive) setReport(null); });
    return () => { alive = false; };
  }, [reportId]);

  if (!rehab) return null;

  const modules = report?.modules || [];
  const red = modules.filter((m) => m.status === "red").length;
  const orange = modules.filter((m) => m.status === "orange").length;
  const modColor = (s) => (s === "red" ? "#EF4444" : s === "orange" ? "#F59E0B" : HULK);

  return (
    <section data-testid="rehab-panel" className="force-dark mt-4 overflow-hidden rounded-2xl border" style={{ borderColor: `${HULK}44`, background: "linear-gradient(180deg,#0B132B,#0F2540)" }}>
      <div className="flex items-center gap-2 px-5 py-3" style={{ background: `${HULK}18`, borderBottom: `1px solid ${HULK}33` }}>
        <Wrench size={18} style={{ color: HULK }} />
        <h3 className="font-display text-sm font-extrabold uppercase tracking-wide" style={{ color: HULK }}>
          Electric Rehab — Opportunité de réparation
        </h3>
      </div>

      <div className="grid gap-4 p-5 sm:grid-cols-3">
        <div data-testid="rehab-defect" className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
          <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-white/50"><AlertTriangle size={13} className="text-amber-400" /> Nature du défaut</div>
          <div className="text-sm font-bold text-white">{rehab.defect || "Défaut batterie à diagnostiquer"}</div>
        </div>
        <div data-testid="rehab-estimate" className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
          <div className="mb-1 text-xs font-semibold text-white/50">Estimation de réparation</div>
          <div className="text-lg font-extrabold" style={{ color: HULK }}>
            {rehab.repair_estimate ? `≈ ${formatPrice(rehab.repair_estimate)}` : "Sur devis"}
          </div>
        </div>
        <div data-testid="rehab-drivable" className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
          <div className="mb-1 text-xs font-semibold text-white/50">Véhicule roulant ?</div>
          <div className="flex items-center gap-1.5 text-sm font-bold text-white">
            {rehab.drivable === true ? <><CheckCircle2 size={16} className="text-emerald-400" /> Oui, roulant</>
              : rehab.drivable === false ? <><XCircle size={16} className="text-red-400" /> Non roulant</>
                : <span className="text-white/60">Non précisé</span>}
          </div>
        </div>
      </div>

      {reportId && modules.length > 0 && (
        <div className="border-t border-white/10 px-5 pb-5 pt-4" data-testid="rehab-battery-schematic">
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5 text-sm font-bold text-white"><BatteryCharging size={16} style={{ color: HULK }} /> Diagnostic batterie — {modules.length} modules</div>
            {red > 0 && <span className="rounded-full bg-red-500/20 px-2.5 py-0.5 text-xs font-bold text-red-300">{red} module{red > 1 ? "s" : ""} défectueux</span>}
            {orange > 0 && <span className="rounded-full bg-amber-500/20 px-2.5 py-0.5 text-xs font-bold text-amber-300">{orange} à surveiller</span>}
          </div>
          <div className="grid grid-cols-8 gap-1.5 sm:grid-cols-12" data-testid="rehab-module-grid">
            {modules.map((m, i) => (
              <div key={m.module_id || i} title={`${m.module_id || `M${i + 1}`} · ${m.voltage ?? "?"} V${m.temperature != null ? ` · ${m.temperature} °C` : ""}`}
                data-testid={`rehab-module-${i}`}
                className="flex aspect-square items-center justify-center rounded-md text-[9px] font-bold"
                style={{ background: `${modColor(m.status)}22`, border: `1.5px solid ${modColor(m.status)}`, color: modColor(m.status) }}>
                {(m.module_id || `M${i + 1}`).toString().replace(/^M?0?/, "")}
              </div>
            ))}
          </div>
          <a href={`/verify/${reportId}`} className="mt-3 inline-block text-xs font-semibold underline" style={{ color: HULK }} data-testid="rehab-report-link">
            Voir le passeport batterie certifié →
          </a>
        </div>
      )}
    </section>
  );
};

export default RehabPanel;
